<template>
  <div
    class="min-h-full grid"
    :class="{
      'grid-rows-[auto_1fr_auto]':
        contentType === 'plain-text' &&
        (hasMixedMedia || hasTikTokImageError || images?.length || videos?.length),
      'grid-rows-[1fr_auto]':
        (contentType === 'plain-text' &&
          !images?.length &&
          !videos?.length &&
          !hasMixedMedia &&
          !hasTikTokImageError) ||
        contentType !== 'plain-text',
    }"
    data-testid="content-edit-input-section"
  >
    <!-- Mixed media warning banner -->
    <div v-if="hasMixedMedia" class="mx-2 sm:mx-4 my-2 sm:my-4">
      <MABanner
        type="warning"
        :title="$t('mixedMediaTitle')"
        :message="$t('mixedMediaWarning')"
      />
    </div>

    <!-- TikTok image not supported banner -->
    <div v-if="hasTikTokImageError" class="mx-2 sm:mx-4 my-2 sm:my-4">
      <MABanner type="warning" :title="$t('tiktokVideoRequired')" />
    </div>

    <!-- videos -->
    <div
      v-if="contentType === 'plain-text' && videos?.length"
      class="flex flex-wrap gap-6 px-2 sm:px-4 pb-2 max-h-48 overflow-y-auto"
    >
      <p class="text-xs opacity-70 w-full mb-1">
        {{ videos.length }} {{ videos.length === 1 ? "video" : "videos" }}
      </p>
      <div
        v-for="video in videos"
        :key="video"
        role="button"
        class="group relative shrink-0"
      >
        <div class="absolute scale-75 z-10 top-0 right-0 hidden group-hover:block">
          <AppButton icon="pi pi-trash" variant="danger" @click="videoToRemove = video" />
        </div>
        <div
          class="w-[170px] h-[170px] group-hover:brightness-75 duration-300 relative border dark:border-surface-700 rounded-md overflow-hidden"
          :aria-label="'Preview video'"
          @click.stop="previewVideoUrl = video"
        >
          <video :src="video" class="w-full h-full" muted preload="metadata" />
        </div>
      </div>
    </div>
    <!-- images -->
    <div
      v-if="contentType === 'plain-text' && images?.length"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 px-2 sm:px-4 max-h-[340px] overflow-y-auto"
    >
      <p class="text-xs opacity-70 col-span-full mb-1">
        {{ images.length }} {{ images.length === 1 ? "image" : "images" }}
      </p>
      <div
        v-for="image in images"
        :key="image"
        role="button"
        class="group relative w-11/12 sm:max-w-60 lg:max-w-xs"
      >
        <div class="absolute scale-75 z-10 top-0 right-0 hidden group-hover:block">
          <AppButton icon="pi pi-trash" variant="danger" @click="imageToRemove = image" />
        </div>
        <img
          :src="image"
          alt="Selected media"
          class="w-full aspect-square object-cover group-hover:brightness-75 duration-300 relative border dark:border-surface-700 rounded-md"
          @click="previewUrl = image"
        />
      </div>
    </div>
    <div v-if="contentType === 'plain-text'" class="h-full max-h-full">
      <textarea
        ref="textarea"
        v-model="content"
        autofocus
        :disabled="loading"
        class="p-2 py-2 sm:py-14 text-lg sm:p-4 block w-full border-transparent ring-transparent outline-hidden focus-visible:outline-none focus-visible:ring-transparent focus:ring-transparent focus:outline-transparent focus:border-transparent resize-none dark:bg-transparent h-full"
        data-testid="content-edit-plain-textarea"
      />
    </div>

    <!-- Markdown -->
    <div v-else class="h-full">
      <MarkdownEditor
        v-model="content"
        :initial-data="inputData"
        :disabled="loading"
        :hide-toolbar="contentType === 'array-list'"
        data-testid="content-edit-markdown-editor"
      />
    </div>

    <div class="flex items-center justify-end gap-4 py-2 sm:py-4">
      <AppButton
        :label="$t('buttons.cancel')"
        variant="secondary"
        text
        class="cancel-edit"
        :disabled="loading"
        data-testid="content-edit-cancel-button"
        @click="emit('close')"
      />
      <AppButton
        :label="$t('buttons.save')"
        :disabled="loading || hasMixedMedia || hasTikTokImageError"
        :loading="loading"
        class="save-edit"
        data-testid="content-edit-save-button"
        @click="emit('save', content)"
      />
    </div>

    <ContentEditMediaPreview
      v-if="previewUrl"
      :url="previewUrl"
      media-type="image"
      full-screen
      @exit-full-screen="previewUrl = ''"
    />

    <!-- Video preview modal -->
    <ModalBlank
      v-if="previewVideoUrl"
      wrapper-class="bg-white dark:bg-slate-800 rounded-lg shadow-lg overflow-auto max-w-2xl w-full max-h-full"
      @close-modal="previewVideoUrl = ''"
    >
      <span class="absolute top-4 right-4">
        <AppButton variant="danger" icon="pi pi-times" @click="previewVideoUrl = ''" />
      </span>
      <div class="w-full relative p-4">
        <ShortVideoPlayer :url="previewVideoUrl" :full-details="true" class="w-full" />
      </div>
    </ModalBlank>

    <GenericModal
      v-if="imageToRemove"
      title="Remove image"
      message="Are you sure you want to remove this image?"
      type="danger"
      @confirm="removeImage(imageToRemove)"
      @cancel="imageToRemove = ''"
    >
      <div>
        <img
          :src="imageToRemove"
          alt="Image to remove"
          class="h-20 w-20 -mt-6 rounded-md"
        />
      </div>
    </GenericModal>

    <GenericModal
      v-if="videoToRemove"
      title="Remove video"
      message="Are you sure you want to remove this video?"
      type="danger"
      @confirm="removeVideo(videoToRemove)"
      @cancel="videoToRemove = ''"
    >
      <div>
        <video :src="videoToRemove" class="h-20 w-32 -mt-6 rounded-md object-cover" />
      </div>
    </GenericModal>
  </div>
</template>

<script setup lang="ts">
import MarkdownEditor from "@/components/markdown/MarkdownEditor.vue";
import type { EditableContentType } from "@/utils/content";
import ContentEditMediaPreview from "./attachments/ContentEditMediaPreview.vue";
import GenericModal from "@/components/shared/GenericModal.vue";
import ModalBlank from "@/partials/ModalBlank.vue";
import ShortVideoPlayer from "@/components/shared/ShortVideoPlayer.vue";
import MABanner from "@/components/primevueComponents/MABanner.vue";

const props = defineProps<{
  loading?: boolean;
  initialData?: string | string[];
  images?: string[];
  videos?: string[];
  contentType: EditableContentType;
  taskType?: string;
  maxNumberOfImages?: number;
  maxNumberOfVideos?: number;
}>();

const emit = defineEmits<{
  close: [];
  save: [string];
  removeImage: [string];
  removeVideo: [string];
}>();

const content = ref("");
const previewUrl = ref("");
const imageToRemove = ref("");
const previewVideoUrl = ref("");
const videoToRemove = ref("");
const textarea = ref<HTMLTextAreaElement | null>(null);

const isTikTok = computed(() => props.taskType?.includes("/tiktok/"));

const hasMixedMedia = computed(() => {
  const hasMultipleMediaTypes = !!(props.images?.length && props.videos?.length);
  const isInstagramPost = props.taskType === "social-media/instagram/write-post";
  return hasMultipleMediaTypes && !isInstagramPost;
});

const hasTikTokImageError = computed(() => {
  if (!isTikTok.value) return false;
  return !!props.images?.length;
});

const inputData = computed(() => {
  if (!props.initialData) {
    return "";
  }
  if (Array.isArray(props.initialData)) {
    return arrayToMarkdownList(props.initialData);
  }
  return props.initialData;
});

function removeImage(image: string) {
  emit("removeImage", image);
  imageToRemove.value = "";
}

function removeVideo(video: string) {
  emit("removeVideo", video);
  videoToRemove.value = "";
}

onBeforeMount(() => {
  content.value = inputData.value;
});

onMounted(() => {
  nextTick(() => {
    if (!textarea.value) return;
    textarea.value.focus();
  });
});
</script>
